import * as Yup from "yup";

/**
 * VAT selection for a product/service line of the proforma & services modal.
 *
 * The exempt branch used to assign the whole filtered array to `vat`, a field
 * read everywhere as an object, so `values.vat.id` was undefined and the line
 * travelled to the backend with no VAT.
 *
 * `options` feeds the (disabled) VAT react-select and must stay an array;
 * `value` is the single option written into the form.
 */
export const resolveProductVatSelection = ({ doNotApplyVat, product, vatOptions, defaultVatOption }) => {
  if (doNotApplyVat) {
    const exemptOptions = (vatOptions ?? []).filter((option) => option?.rate === 0);
    return { options: exemptOptions, value: exemptOptions[0] ?? null };
  }

  const value = product?.vat ?? defaultVatOption ?? null;
  return { options: value ? [value] : [], value };
};

/**
 * Validation schema of the proforma & services document-line modal.
 *
 * @param {(key: string, vars?: object) => string} t translation function
 */
export const getProformaDocumentLineValidationSchema = (t) =>
  Yup.object({
    type_of_product: Yup.string().required(
      `${t("common.please")} ${t("common.select")} ${t("common.typeOfProduct")}`,
    ),
    product: Yup.object()
      .when("type_of_product", {
        is: (type_of_product) => type_of_product === "product" || type_of_product === "service",
        then: Yup.object()
          .required(`${t("common.please")} ${t("common.select")} ${t("common.product/service")}`)
          .nullable(),
      })
      .nullable(),
    quantity: Yup.number().when("type_of_product", {
      is: (type_of_product) => type_of_product === "product" || type_of_product === "service",
      then: Yup.number()
        .min(1, t("validation.numberMin", { totalNumber: 1 }))
        .required(`${t("common.please")} ${t("common.enter")} ${t("common.quantity")}`),
    }),
    auction: Yup.object()
      .when(["type_of_product", "from_type"], {
        is: (type_of_product, from_type) => type_of_product === "lot" && from_type === "auction",
        then: Yup.object()
          .required(`${t("common.please")} ${t("common.select")} ${t("common.auction")}`)
          .nullable(),
      })
      .nullable(),
    paddle: Yup.object()
      .when(["type_of_product", "from_type"], {
        is: (type_of_product, from_type) => type_of_product === "lot" && from_type === "auction",
        then: Yup.object()
          .required(`${t("common.please")} ${t("common.select")} ${t("common.paddle")}`)
          .nullable(),
      })
      .nullable(),
    discount: Yup.number()
      .min(0, t("validation.numberMin", { totalNumber: 0 }))
      .when("discount_unit", {
        is: (discount_unit) => discount_unit === "%",
        then: Yup.number().max(100, t("validation.numberMax", { totalNumber: 100 })),
      }),
    premium: Yup.number().min(0, t("validation.numberMin", { totalNumber: 0 })),
    vat: Yup.object()
      .required(`${t("common.please")} ${t("common.select")} ${t("common.vat_another")}`)
      .nullable(),
    // tax_exemption_code: Yup.string().when("vat", {
    //   is: (vat) =>
    //     vat.rate === 0 ,
    //   then: Yup.string()
    //     .required(
    //       `${t("common.please")} ${t("common.select")} ${t("common.taxExemptionCode")}`
    //     ),
    // }),
  });
